"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import styles from "./CustomCard.module.scss";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import PriceBreakoutSection from "../PriceBreakoutSection";
import DeliveryCard from "../DeliveryCard";
import FeatureTag from "@components/atoms/FeatureTag";
import assetLoader from "@utils/internalImageLoder";
import { formatCurrencyINR, formatPrice, maskAmount } from "@utils/numberFormatter";
import { IVariantDetailsPrice } from "./CustomCard.types";
import { fetchPriceBreakup } from "@services/getPriceBreakup";
import { useRouter } from "next/navigation";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { getSellableDetails, getNonSellableDetails } from "@helpers/priceBreakupHelper";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";
import CustomLink from "@components/atoms/Link";

interface CustomCardProps {
  data: IVariantDetailsPrice;
  isExpanded: boolean;
  onToggle: () => void;
  isLoggedIn: boolean;
  city: any;
  brandName: string;
  modelName: string;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
  firstVariantPriceBreakup?: any;
  expandedIndex?: number;
}

interface PriceBreakupData {
  charges: { label: string; value: string | null }[];
  discounts: { label: string; value: string | null }[];
  finalPrice: string;
  isExpressDelivery: boolean;
  deliveryDate: string;
  consumerSavings?: string;
}

export default function CustomCard({
  data,
  isExpanded,
  onToggle,
  isLoggedIn,
  city,
  brandName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  savingBalance,
  firstVariantPriceBreakup,
  expandedIndex,
}: CustomCardProps) {
  const cityName = city?.name || "";
  const router = useRouter();
  const [priceBreakup, setPriceBreakup] = useState<PriceBreakupData>(firstVariantPriceBreakup);
  const cityId = city?.id || 3;
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (isExpanded && (expandedIndex !== 0 || !firstVariantPriceBreakup)) {
      const fetchBreakup = async () => {
        try {
          setLoading(true);
          const res = await fetchPriceBreakup(data.slug, cityId);
          if (isSellable) setPriceBreakup(getSellableDetails(res, data, isLoggedIn, city));
          else setPriceBreakup(getNonSellableDetails(res, city));
        } catch (error) {
          console.error("Failed to fetch price breakup", error);
        } finally {
          setLoading(false);
        }
      };

      fetchBreakup();
    }
  }, [isExpanded]);

  return (
    <div className={styles.rootContainer}>
      {/* Section 1 - Title and Price */}
      <div className={styles.cardHeader}>
        <div className={styles.cardTopRow}>
          <CustomLink
            href={`/cars/${data.slug}`}
            className={styles.variantTitleLink}
          >
            <Text level="p" className={styles.variantTitle}>
              {data.variantName}
            </Text>
          </CustomLink>

          <div className={styles.priceGroup}>
            <Text
              level="p"
              className={
                isSellable && isLoggedIn
                  ? styles.strikePrice
                  : styles.currentPrice
              }
            >
              {`${formatPrice(data.onRoadPrice)}`}
            </Text>
            {isSellable && isLoggedIn && (
              <Text level="p" className={styles.currentPrice}>
                {isLoggedIn
                  ? `${formatPrice(data.adspPrice)}`
                  : `₹${formatPrice(data.onRoadPrice)}`}
              </Text>
            )}
          </div>
        </div>

        <div className={styles.cardSubInfoWrapper}>
          <div className={styles.cardSubInfoRow}>
            <Text level="p" className={styles.fuelText}>
              {`${data.fuelType} • ${data.transmission}`}
            </Text>
            {isSellable && (
              <Text level="p" className={styles.savingsText}>
                {`Save up to ${
                  isLoggedIn
                    ? formatCurrencyINR(data.ackoDriveSavings ?? 0)
                    : maskAmount(data.ackoDriveSavings ?? 0)
                }`}
              </Text>
            )}
          </div>
        </div>

        <div className={styles.featuresWrapper}>
          <div className={styles.featuresContainer}>
            {data.featureItemDtos.map((feature) => (
              <FeatureTag
                key={feature.name}
                feature={`${feature.name} ${
                  feature.value ? ` - ${feature.value}` : ""
                }`}
              />
            ))}
          </div>
          <Button className={styles.showMoreButton} onClick={onToggle}>
            <Image
              src={`${assetLoader()}/icons/showmore.png`}
              loading="lazy"
              alt="Toggle"
              width={32}
              height={32}
              className={`${styles.icon} ${
                isExpanded ? styles.iconRotate : ""
              }`}
            />
          </Button>
        </div>
      </div>

      {/* Section 4 - Expandable Content */}
      <div
        className={`${styles.expandableWrapper} ${
          isExpanded ? styles.expanded : ""
        }`}
      >
        {loading ? (
          <div className={styles.loader}>Loading price breakup...</div>
        ) : (
          priceBreakup && (
            <div className={styles.wrapper}>
              <div className={styles.container}>
                <PriceBreakoutSection
                  charges={priceBreakup.charges}
                  discounts={priceBreakup.discounts}
                  finalPrice={priceBreakup.finalPrice}
                  consumerSavings={priceBreakup.consumerSavings}
                  isSellable={isSellable}
                  savingBalance={savingBalance}
                />

                <div className={styles.rightBox}>
                  {isSellable && (
                    <>
                      <DeliveryCard
                        expressDelivery={priceBreakup.isExpressDelivery}
                        deliveryDate={priceBreakup.deliveryDate}
                        isLoggedIn={isLoggedIn}
                        pageType={pageType}
                        brandName={brandName}
                        modelName={modelName}
                        variantName={data?.variantName}
                        carAvailability={carAvailability}
                      />
                      <div className={styles.buttonWrapper}>
                        <Button
                          className={styles.buttonOutline}
                          onClick={async () => {
                            await trackEventWithCommonProps(
                              "action",
                              EventType.TRACK_EVENT,
                              {
                                pageType: pageType || "",
                                additionalProps: {
                                  action_type: "tap_btn",
                                  action_name: "tap_btn_variants_see_details",
                                  section: "Variant",
                                  variant_name: data?.variantName || "",
                                  car_availability: carAvailability || "",
                                  vehicle_make: brandName || "",
                                  vehicle_model: modelName || "",
                                  city_name: cityName || "",
                                },
                              }
                            );
                            router.push(`/cars/${data.slug}`);
                          }}
                        >
                          {`Explore ${data.variantName}`}
                        </Button>
                        {!isLoggedIn && (
                          <Button
                            className={styles.buttonPrimary}
                            onClick={async () => {
                              await trackEventWithCommonProps(
                                "action",
                                EventType.TRACK_EVENT,
                                {
                                  pageType: pageType || "",
                                  additionalProps: {
                                    action_type: "tap_btn",
                                    action_name: "tap_btn_login_to_save",
                                    section: "Variant",
                                    variant_name: data?.variantName || "",
                                    car_availability: carAvailability || "",
                                    vehicle_make: brandName || "",
                                    vehicle_model: modelName || "",
                                    city_name: cityName || "",
                                  },
                                }
                              );

                              const mmvDetails = {
                                make: brandName,
                                model: modelName,
                                variant: data?.variantName || "",
                                color: "",
                              };
                              if (typeof window !== "undefined") {
                                sessionStorage.setItem(
                                  "loginType",
                                  "Reveal price"
                                );
                                 localStorage.setItem(
                                   "MMV",
                                   JSON.stringify({
                                     make: brandName,
                                     model: modelName,
                                     variant: data?.variantName || "",
                                     color: ""
                                   })
                                 );
                              }
                              const loginUrl = getLoginUrl(
                                addCaptureIntentIfNeeded(
                                  window.location.href,
                                  mmvDetails,
                                  "Reveal price",
                                  pageType
                                )
                              );
                              await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
                            }}
                          >
                            Check ACKO Drive price
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                  {!isSellable && (
                    <div className={styles.nonSellableButtonWrapper}>
                      <Button
                        className={styles.buttonOutline}
                        onClick={async () => {
                          await trackEventWithCommonProps(
                            "action",
                            EventType.TRACK_EVENT,
                            {
                              pageType: pageType || "",
                              additionalProps: {
                                action_type: "tap_btn",
                                action_name: "tap_btn_variants_see_details",
                                section: "Variant",
                                variant_name: data?.variantName || "",
                                car_availability: carAvailability || "",
                                vehicle_make: brandName || "",
                                vehicle_model: modelName || "",
                                city_name: cityName || "",
                              },
                            }
                          );
                          router.push(`/cars/${data.slug}`);
                        }}
                      >
                        {`Explore ${data.variantName}`}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}